import { AVAILABLE_METRICS, METRIC_CATEGORIES, metricsByCategory } from '@/lib/reporting/metrics';
import { TYPE, UI_MUTED } from '@/lib/typography';
import { cn } from '@/utils/cn';
import type { TFunction } from 'i18next';

interface MetricSelectorProps {
  selectedIds: string[];
  onChange: (ids: string[]) => void;
  onReset?: () => void;
  t: TFunction;
}

export function MetricSelector({ selectedIds, onChange, onReset, t }: MetricSelectorProps) {
  const toggle = (id: string) => {
    onChange(
      selectedIds.includes(id) ? selectedIds.filter((item) => item !== id) : [...selectedIds, id],
    );
  };

  const allIds = AVAILABLE_METRICS.map((metric) => metric.id);
  const allSelected = allIds.every((id) => selectedIds.includes(id));

  return (
    <div className="space-y-4">
      <div className="flex justify-end gap-3">
        {onReset && (
          <button
            type="button"
            onClick={onReset}
            className={`${UI_MUTED} hover:text-ds-text`}
          >
            {t('reports.metrics_reset')}
          </button>
        )}
        <button
          type="button"
          onClick={() => onChange(allSelected ? [] : allIds)}
          className={`${UI_MUTED} hover:text-ds-text`}
        >
          {allSelected ? t('reports.metrics_clear_group') : t('reports.metrics_select_all')}
        </button>
      </div>
      {METRIC_CATEGORIES.map((category) => {
        const metrics = metricsByCategory(category);
        return (
          <section key={category}>
            <h3 className={`mb-2 ${UI_MUTED}`}>
              {t(`reports.categories.${category}`)}
            </h3>
            <ul className="grid gap-x-6 gap-y-1.5 sm:grid-cols-2">
              {metrics.map((metric) => {
                const checked = selectedIds.includes(metric.id);
                return (
                  <li key={metric.id}>
                    <label
                      className="flex cursor-pointer items-center gap-2"
                      title={t(metric.descriptionKey)}
                    >
                      <input
                        type="checkbox"
                        checked={checked}
                        onChange={() => toggle(metric.id)}
                        className="h-3.5 w-3.5 rounded border-ds-field text-ds-teal focus:ring-ds-teal"
                      />
                      <span
                        className={cn(
                          TYPE.body,
                          checked
                            ? 'text-ds-text'
                            : 'text-ds-muted',
                        )}
                      >
                        {t(metric.labelKey)}
                      </span>
                    </label>
                  </li>
                );
              })}
            </ul>
          </section>
        );
      })}
    </div>
  );
}
